import React, { useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import toast from "react-hot-toast";
import { useApi } from "../../context/ApiClient";
import { useCenter } from "../../context/CenterContext";
import { useLocation } from "react-router-dom";

const UpdateACMarksConfirmModal = ({
  isOpen,
  candidates,
  onConfirm,
  onCancel,
}) => {
  const modalRef = useRef(null);
  const api = useApi(process.env.REACT_APP_AMS_BASE_URL);
  const { centerNumber } = useCenter();
  const location = useLocation();
  const { unit, sessionId } = location.state || {};

  const handleCancel = React.useCallback(() => {
    toast.success("Your changes have been saved");
    onCancel();
  }, [onCancel]);

  useEffect(() => {
    const handler = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) {
        handleCancel();
      }
    };
    if (isOpen) document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen, handleCancel]);

  if (!isOpen || !candidates?.length) return null;

  const candidate = candidates[0];
  const unitCode = unit.split(" ")[0];

  const handleConfirm = async () => {
    const params = new URLSearchParams();
    params.append("assessment_code", unitCode);
    params.append(
      "centre_candidate_number",
      candidate.centreCandidateNumber ?? candidate.centreCandidateNumber,
    );
 
    params.append("centre_number", candidate.candidateCentreNumber ?? centerNumber);
    params.append("session_id", sessionId);

    params.append("total_criteria_score", candidate.achievedCount);
    params.append("stage_id", 3);

    const response = await api.put(
      `updateTotalCriteriaScore?${params.toString()}`,
      {},
    );

    if (response.status === 200) {
      toast.success("Your changes have been saved");
      onConfirm();
    } else {
      toast.error("Failed to update total marks");
    }
  };

  return ReactDOM.createPortal(
    <div className="modal is-active">
      <div className="modal-background" onClick={handleCancel} />
      <div className="modal-card" ref={modalRef}>
        <header className="modal-card-head">
          <p className="modal-card-title">
            Criteria achieved does not match updated total marks
          </p>
        </header>

        <section className="modal-card-body">
          <div className="block">
            <div className="box">
              <div className="content">
                <p>
                  <span data-testid="original-marks-value">
                    You provided an updated total mark of
                  </span>{" "}
                  <strong>{candidate.candidateMarks}</strong> for this
                  candidate.
                </p>
                <p>
                  <span data-testid="achieved-count">You have now entered</span>{" "}
                  <strong>{candidate.achievedCount}</strong> based on the
                  criteria achieved.
                </p>

                <p>
                  <span data-testid="confirm-update">
                    Please confirm you want to amend the updated total marks to{" "}
                    <strong>{candidate.achievedCount}</strong>.
                  </span>
                </p>
                <hr></hr>
                <p>
                  <span data-testid="final-update">
                    Please note that we may contact you about corrections made
                    at this stage.
                  </span>
                </p>
              </div>
            </div>
          </div>
        </section>

        <footer className="modal-card-foot">
          <button className="button is-success" onClick={handleConfirm}>
            Confirm
          </button>
          <button className="button" onClick={handleCancel}>
            Cancel
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
};

UpdateACMarksConfirmModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  candidates: PropTypes.array.isRequired,
  onConfirm: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

export default UpdateACMarksConfirmModal;
